import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// MUI
import { IconButton, Tooltip } from "@mui/material";

import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import { HiOutlineBuildingOffice2, HiOutlineUser } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { PROTECTED_ROUTES } from "routes";

// Components
import {
  copyLinkItem,
  openInNewTabItem,
  openItem,
  useContextMenu,
} from "components/ui/ContextMenu";

// Hooks
import useHotkeys, { hotkeyToText } from "hooks/ui/useHotkeys";

import { getCompanies } from "api/companies";
import { getOperators } from "api/operators";

// Utils
import { colors } from "styles/tokens";

// Cache for API responses with TTL
const cache = {
  operators: { data: null, timestamp: null },
  companies: { data: null, timestamp: null },
};

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 300; // 300ms debounce

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const SEARCH_SHORTCUT = "mod+shift+f";

const UnifiedSearch = ({ open, onExpand }) => {
  const { t } = useTranslation();
  const menu = useContextMenu();
  const [searchTerm, setSearchTerm] = useState("");
  const [operators, setOperators] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredResults, setFilteredResults] = useState({ operators: [], companies: [] });
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showAllOperators, setShowAllOperators] = useState(false);
  const [showAllCompanies, setShowAllCompanies] = useState(false);

  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const inputRef = useRef(null);
  const focusAfterExpand = useRef(false);
  const navigate = useNavigate();

  // Cmd/Ctrl+Shift+F puts the cursor in the search ("/" is taken by the notes page).
  // Collapsed, the sidebar opens first and the field gets the focus once it exists.
  const focusSearch = useCallback(() => {
    if (open) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else {
      focusAfterExpand.current = true;
      onExpand?.();
    }
  }, [open, onExpand]);
  useEffect(() => {
    if (open && focusAfterExpand.current) {
      focusAfterExpand.current = false;
      inputRef.current?.focus();
    }
  }, [open]);
  useHotkeys(
    {
      [SEARCH_SHORTCUT]: {
        handler: focusSearch,
        description: t("shell:shortcuts.searchSidebar"),
      },
    },
    { group: t("ui:shortcuts.groups.navigation") }
  );

  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  // Fetch data with caching and search
  const fetchData = useCallback(async (searchStr) => {
    if (!searchStr || searchStr.length < 3) {
      setOperators([]);
      setCompanies([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const now = Date.now();
    const cacheKey = `search_${searchStr}`;

    try {
      const promises = [];

      // Fetch operators
      promises.push(
        getOperators().then((res) => {
          const data = res.data || [];
          return data;
        })
      );

      // Fetch companies with search parameter
      promises.push(
        getCompanies({ name: searchStr, offset: 0, limit: 5 }).then((res) => {
          // Handle paginated response structure
          if (res && res.results && Array.isArray(res.results)) {
            return res.results;
          } else if (res && res.data && Array.isArray(res.data)) {
            return res.data;
          } else if (res && res.data && Array.isArray(res.data.data)) {
            return res.data.data;
          } else if (Array.isArray(res)) {
            return res;
          }
          return [];
        })
      );

      const [operatorsData, companiesData] = await Promise.all(promises);
      setOperators(operatorsData);
      setCompanies(companiesData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setOperators([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoized filter function
  const filterData = useMemo(() => {
    return (searchStr) => {
      if (!searchStr) return { operators: [], companies: [] };

      const search = searchStr.toLowerCase();

      // Filter operators (ensure it's an array)
      const operatorsArray = Array.isArray(operators) ? operators : [];
      const filteredOps = operatorsArray.filter((operator) => {
        const fullName = `${operator.first_name || ""} ${operator.last_name || ""}`.toLowerCase();
        const email = (operator.email || "").toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });

      // Filter companies (ensure it's an array)
      const companiesArray = Array.isArray(companies) ? companies : [];
      const filteredComps = companiesArray.filter((company) => {
        const name = (company.name || "").toLowerCase();
        const code = (company.code || "").toLowerCase();
        return name.includes(search) || code.includes(search);
      });

      return {
        operators: filteredOps,
        companies: filteredComps,
      };
    };
  }, [operators, companies]);

  // Fetch data when search term changes (min 3 characters)
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
      fetchData(debouncedSearchTerm);
    } else {
      setOperators([]);
      setCompanies([]);
      setFilteredResults({ operators: [], companies: [] });
      setShowResults(false);
    }
  }, [debouncedSearchTerm, fetchData]);

  // Filter results based on fetched data
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
      const results = filterData(debouncedSearchTerm);
      setFilteredResults(results);
      setShowResults(true);
      setSelectedIndex(-1);
    }
  }, [debouncedSearchTerm, filterData, operators, companies]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setShowAllOperators(false);
        setShowAllCompanies(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!showResults) return;

      const totalOperators = showAllOperators
        ? filteredResults.operators.length
        : Math.min(5, filteredResults.operators.length);
      const totalCompanies = showAllCompanies
        ? filteredResults.companies.length
        : Math.min(5, filteredResults.companies.length);
      const totalItems =
        totalOperators +
        totalCompanies +
        (filteredResults.operators.length > 5 ? 1 : 0) +
        (filteredResults.companies.length > 5 ? 1 : 0);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % totalItems);
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev <= 0 ? totalItems - 1 : prev - 1));
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0) {
            const operatorShowMoreIndex = Math.min(5, filteredResults.operators.length);
            const companiesStartIndex =
              filteredResults.operators.length > 5
                ? operatorShowMoreIndex + 1
                : operatorShowMoreIndex;

            if (selectedIndex < totalOperators) {
              handleOperatorClick(filteredResults.operators[selectedIndex].id);
            } else if (
              selectedIndex === operatorShowMoreIndex &&
              filteredResults.operators.length > 5
            ) {
              setShowAllOperators(true);
            } else if (selectedIndex < companiesStartIndex + totalCompanies) {
              const companyIndex = selectedIndex - companiesStartIndex;
              handleCompanyClick(filteredResults.companies[companyIndex].id);
            } else if (
              selectedIndex ===
                companiesStartIndex + Math.min(5, filteredResults.companies.length) &&
              filteredResults.companies.length > 5
            ) {
              setShowAllCompanies(true);
            }
          }
          break;
        case "Escape":
          setShowResults(false);
          searchRef.current?.querySelector("input")?.blur();
          break;
        default:
          break;
      }
    },
    [showResults, filteredResults, selectedIndex, showAllOperators, showAllCompanies]
  );

  const handleOperatorClick = (operatorId) => {
    navigate(PROTECTED_ROUTES.OPERATOR_BOARD(operatorId));
    setSearchTerm("");
    setShowResults(false);
  };

  const handleCompanyClick = (companyId) => {
    navigate(PROTECTED_ROUTES.CLIENT_BOARD(companyId));
    setSearchTerm("");
    setShowResults(false);
  };

  // Right-click on a result: open in a new tab / copy link
  const resultMenu = (path) =>
    menu.bind(() => [openItem(path), openInNewTabItem(path), copyLinkItem(path)]);

  if (!open) {
    return onExpand ? (
      <div className="flex justify-center px-3">
        <Tooltip
          title={`${t("shell:sidebar.searchCollapsed")} (${hotkeyToText(SEARCH_SHORTCUT)})`}
          placement="right"
        >
          <IconButton
            onClick={focusSearch}
            aria-label={t("shell:sidebar.searchCollapsed")}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              color: colors.gray500,
              "&:hover": { backgroundColor: colors.gray100, color: colors.gray900 },
            }}
          >
            <FiSearch size={20} />
          </IconButton>
        </Tooltip>
      </div>
    ) : null;
  }

  const displayedOperators = showAllOperators
    ? filteredResults.operators
    : filteredResults.operators.slice(0, 5);
  const displayedCompanies = showAllCompanies
    ? filteredResults.companies
    : filteredResults.companies.slice(0, 5);

  return (
    <div ref={searchRef} className="relative px-3">
      <FiSearch
        size={16}
        aria-hidden="true"
        className="pointer-events-none absolute top-[18px] left-6 -translate-y-1/2 text-gray-500"
      />
      <input
        ref={inputRef}
        type="text"
        aria-label={t("shell:sidebar.searchAria")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => searchTerm && searchTerm.length >= 3 && setShowResults(true)}
        onKeyDown={handleKeyDown}
        placeholder={t("navigation:unifiedSearch.placeholder")}
        className="field field--search pr-12"
      />
      {!searchTerm && (
        <kbd className="kbd pointer-events-none absolute top-[8px] right-[22px]" aria-hidden="true">
          {hotkeyToText(SEARCH_SHORTCUT)}
        </kbd>
      )}
      {searchTerm && searchTerm.length < 3 ? (
        <p className="mt-1 px-2 text-xs text-gray-500">
          {t("navigation:unifiedSearch.minCharacters")}
        </p>
      ) : !searchTerm ? (
        <p className="mt-1 px-2 text-xs text-gray-500">
          {t("navigation:unifiedSearch.companiesOrOperators")}
        </p>
      ) : null}

      {showResults && (
        <div
          ref={resultsRef}
          className="absolute left-3 right-3 z-10 mt-1 flex max-h-[min(400px,calc(100dvh-160px))] flex-col overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white shadow-popover"
        >
          {loading ? (
            <div className="px-3 py-4">
              <div className="animate-pulse motion-reduce:animate-none">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ) : (
            <>
              {/* Operators Section */}
              {filteredResults.operators.length > 0 && (
                <div className="w-full border-b border-gray-100">
                  <div className="bg-gray-50 px-3 py-2">
                    <div className="flex items-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <HiOutlineUser className="w-4 h-4 mr-1" />
                      {t("navigation:unifiedSearch.operators")}
                    </div>
                  </div>
                  {displayedOperators.map((operator, index) => (
                    <button
                      key={operator.id}
                      onClick={() => handleOperatorClick(operator.id)}
                      {...resultMenu(PROTECTED_ROUTES.OPERATOR_BOARD(operator.id))}
                      className={`w-full cursor-pointer px-3 py-2 text-left transition-colors duration-150 hover:bg-gray-50 focus-visible:bg-primary-soft focus-visible:outline-none ${
                        selectedIndex === index ? "bg-primary-soft" : ""
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft">
                          <span className="text-xs font-medium text-primary-strong">
                            {operator.first_name?.[0]?.toUpperCase() || "?"}
                            {operator.last_name?.[0]?.toUpperCase() || ""}
                          </span>
                        </div>
                        <div className="ml-3 overflow-hidden">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {operator.first_name} {operator.last_name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{operator.email}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                  {!showAllOperators && filteredResults.operators.length > 5 && (
                    <button
                      onClick={() => setShowAllOperators(true)}
                      className={`w-full cursor-pointer px-3 py-2 text-left text-sm text-primary-strong hover:bg-gray-50 focus-visible:bg-primary-soft focus-visible:outline-none ${
                        selectedIndex === Math.min(5, filteredResults.operators.length)
                          ? "bg-primary-soft"
                          : ""
                      }`}
                    >
                      {t("navigation:unifiedSearch.showMoreOperators", {
                        count: filteredResults.operators.length - 5,
                      })}
                    </button>
                  )}
                </div>
              )}

              {/* Companies Section */}
              {filteredResults.companies.length > 0 && (
                <div className="flex w-full flex-col">
                  <div className="bg-gray-50 px-3 py-2">
                    <div className="flex items-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <HiOutlineBuildingOffice2 className="w-4 h-4 mr-1" />
                      {t("navigation:unifiedSearch.companies")}
                    </div>
                  </div>
                  {displayedCompanies.map((company, index) => {
                    const actualIndex =
                      (showAllOperators
                        ? filteredResults.operators.length
                        : Math.min(5, filteredResults.operators.length)) +
                      (filteredResults.operators.length > 5 && !showAllOperators ? 1 : 0) +
                      index;
                    return (
                      <button
                        key={company.id}
                        onClick={() => handleCompanyClick(company.id)}
                        {...resultMenu(PROTECTED_ROUTES.CLIENT_BOARD(company.id))}
                        className={`w-full cursor-pointer px-3 py-2 text-left transition-colors duration-150 hover:bg-gray-50 focus-visible:bg-primary-soft focus-visible:outline-none ${
                          selectedIndex === actualIndex ? "bg-primary-soft" : ""
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
                            <HiOutlineBuildingOffice2 className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="ml-3 overflow-hidden">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {company.name}
                            </div>
                            {company.code && (
                              <div className="text-xs text-gray-500 truncate">
                                {t("navigation:unifiedSearch.code")}: {company.code}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {!showAllCompanies && filteredResults.companies.length > 5 && (
                    <button
                      onClick={() => setShowAllCompanies(true)}
                      className="w-full cursor-pointer px-3 py-2 text-left text-sm text-primary-strong hover:bg-gray-50 focus-visible:bg-primary-soft focus-visible:outline-none"
                    >
                      {t("navigation:unifiedSearch.showMoreCompanies", {
                        count: filteredResults.companies.length - 5,
                      })}
                    </button>
                  )}
                </div>
              )}

              {/* No results */}
              {filteredResults.operators.length === 0 &&
                filteredResults.companies.length === 0 &&
                searchTerm &&
                searchTerm.length >= 3 && (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">
                    {t("navigation:unifiedSearch.noResults", { searchTerm })}
                  </div>
                )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedSearch;
